import useNotification from 'hooks/useNotification';
import {
  BgColorsOutlined,
  BuildOutlined,
  EditFilled,
  ProfileOutlined,
  SettingOutlined
} from '@ant-design/icons';
import { COURSE_ICONS, COURSE_ICONS_PATH } from 'constants/course-icons';
import {
  Divider,
  Flex,
  Form,
  Image,
  Input,
  InputNumber,
  Modal,
  Radio,
  RadioChangeEvent,
  Select,
  Switch,
  Typography
} from 'antd';
import { FieldsetTitle } from 'components/professor_area/FieldsetTitle';
import { FormItemCol } from 'components/professor_area/FormItemCol';
import { HttpError } from '@refinedev/core';
import { IProductCategory } from 'interfaces/product_categories';
import { IProfessor } from 'interfaces/professor';
import { getTenant } from 'services/tenants';
import { useCallback, useState } from 'react';
import { useModalForm, useSelect } from '@refinedev/antd';

interface CreateGraduateCourseModalProps {
  open: boolean;
  onClose: (close: boolean) => void;
}

interface IGraduateCoursePost {
  name: string;
  kind: 'graduate' | 'postgraduate';
  emec: string;
  duration_in_hours: number;
  minimum_grade: number;
  coordinator_id: string;
  has_vade_mecum: boolean;
  media: {
    icon: string;
    tags?: string[];
    card_image?: string;
    header_image?: string;
    sso_qc: boolean;
  };
}

export const CreateGraduateCourseModal = ({
  open,
  onClose
}: CreateGraduateCourseModalProps) => {
  const { showError, showSuccess } = useNotification();
  const [kind, setKind] = useState<'graduate' | 'undergraduate'>('graduate');
  const [icon, setIcon] = useState(COURSE_ICONS[2]);
  const [courseType, setCourseType] = useState<'regular' | 'on_demand'>(
    'regular'
  );

  const tenant = getTenant();
  const ssoQcDisabled = tenant.disabledFeatures?.includes('sso_qc');

  const { modalProps, formProps } = useModalForm<
    IGraduateCoursePost,
    HttpError,
    IGraduateCoursePost
  >({
    action: 'create',
    resource: 'graduate_courses',
    redirect: false,
    successNotification: false,
    errorNotification: false,
    onMutationSuccess: () => {
      formProps.form?.resetFields();
      showSuccess('Curso cadastrado com sucesso');
      onClose(false);
    },
    onMutationError: _error => {
      const message = 'Não foi possível cadastrar o curso. Tente novamente.';
      showError(message);
    }
  });

  const { selectProps: coordinatorSelectProps } = useSelect<IProfessor>({
    resource: 'professors',
    optionLabel: 'name',
    optionValue: 'id',
    fetchSize: 9999,

    onSearch: value => [
      {
        field: 'filter[by_name]',
        operator: 'eq',
        value
      }
    ],

    pagination: {
      mode: 'server'
    }
  });

  const { selectProps: productCategorySelectProps } =
    useSelect<IProductCategory>({
      resource: 'products/categories',
      optionLabel: 'name',
      optionValue: 'name',

      onSearch: value => [
        {
          field: 'filter[by_name]',
          operator: 'eq',
          value
        }
      ],

      pagination: {
        mode: 'server'
      }
    });

  const onChangeKind = useCallback((e: RadioChangeEvent) => {
    const value = e.target.value as 'graduate' | 'undergraduate';
    setKind(value);
    setIcon(value === 'graduate' ? COURSE_ICONS[2] : COURSE_ICONS[1]);
  }, []);

  const onChangeIcon = useCallback(
    (e: RadioChangeEvent) => {
      const value = e.target.value;
      setIcon(value);
      formProps.form?.setFieldValue(['media', 'icon'], value);
    },
    [formProps.form]
  );

  const onCloseModal = useCallback(() => {
    formProps.form?.resetFields();
    setIcon(COURSE_ICONS[2]);
    setKind('graduate');
    setCourseType('regular');
    onClose(false);
  }, [formProps.form, onClose]);

  return (
    <Modal
      {...modalProps}
      open={open}
      onCancel={onCloseModal}
      title="Novo curso regulado"
      okText="Criar curso"
      cancelText="Voltar"
      width={800}
    >
      <Form {...formProps} layout="vertical">
        <fieldset>
          <FieldsetTitle title="Informações Gerais" icon={ProfileOutlined} />

          <div className="pl-6">
            <FormItemCol
              cols={[6, 18]}
              label="Nome"
              name="name"
              rules={[{ required: true, message: 'O nome é obrigatório' }]}
            >
              <Input placeholder="Ex.: Direito Civil" />
            </FormItemCol>

            <FormItemCol
              cols={[6, 18]}
              label="Tipo de curso"
              name="kind"
              rules={[{ required: true, message: 'O tipo é obrigatório' }]}
              initialValue="graduate"
            >
              <Radio.Group onChange={onChangeKind}>
                <Radio.Button value="graduate">Pós-graduação</Radio.Button>
                {!tenant.disabledFeatures?.includes(
                  'undergraduate_courses'
                ) && (
                  <Radio.Button value="undergraduate">Graduação</Radio.Button>
                )}
              </Radio.Group>
            </FormItemCol>
          </div>
        </fieldset>

        <Divider />

        <fieldset>
          <FieldsetTitle title="Dados do curso" icon={EditFilled} />

          <div className="pl-6">
            <FormItemCol
              cols={[6, 18]}
              label="Código e-MEC"
              name="emec"
              rules={[
                { required: true, message: 'O código e-MEC é obrigatório' }
              ]}
            >
              <Input className="sm:w-[100px]" />
            </FormItemCol>

            <FormItemCol
              cols={[6, 18]}
              label="Duração (horas)"
              name="duration_in_hours"
              rules={[{ required: true, message: 'A duração é obrigatória' }]}
            >
              <InputNumber className="sm:w-[100px]" />
            </FormItemCol>

            <FormItemCol
              cols={[6, 18]}
              label="Nota mínima (0-100)"
              name="min_evaluation_score"
              rules={[
                { required: true, message: 'A nota mínima é obrigatória' }
              ]}
            >
              <InputNumber className="sm:w-[100px]" />
            </FormItemCol>

            <FormItemCol
              cols={[6, 18]}
              label="Coordenador"
              name="professor_id"
              rules={[
                { required: true, message: 'O coordenador é obrigatório' }
              ]}
            >
              <Select
                {...coordinatorSelectProps}
                placeholder="Escolha o coordenador"
                showSearch
              />
            </FormItemCol>
          </div>
        </fieldset>

        <Divider style={{ margin: '16px 0' }} />

        {/* Configuração de curso */}
        <fieldset>
          <FieldsetTitle title="Configuração de curso" icon={SettingOutlined} />
          <div className="pl-6">
            <FormItemCol
              cols={[6, 18]}
              style={{ marginBottom: 16 }}
              label="Tipo de curso"
              name="course_type"
              initialValue="regular"
              rules={[{ required: true }]}
            >
              <Radio.Group
                onChange={e => setCourseType(e.target.value)}
                value={courseType}
              >
                <Radio.Button value="regular">Regular</Radio.Button>
                <Radio.Button value="on_demand">Flexível</Radio.Button>
              </Radio.Group>
            </FormItemCol>

            {courseType === 'on_demand' && (
              <FormItemCol
                cols={[6, 18]}
                style={{ marginBottom: 16 }}
                label="Dias mínimos para aprovação"
                name="minimum_days_to_approve"
                rules={[
                  {
                    required: true,
                    message: 'Campo obrigatório para cursos flexíveis'
                  }
                ]}
              >
                <InputNumber min={0} />
              </FormItemCol>
            )}

            <Typography.Text
              type="secondary"
              style={{ display: 'block', marginBottom: 0 }}
            >
              <strong>Regular:</strong> Curso com datas de início e término definidas. O certificado é liberado somente após o fechamento de todas as turmas.
              <br />
              <strong>Flexível:</strong> O aluno pode iniciar e concluir o curso no seu próprio ritmo. O certificado é liberado após a conclusão do curso, respeitando o prazo mínimo configurado.
            </Typography.Text>
          </div>
        </fieldset>

        <Divider style={{ margin: '16px 0' }} />

        {/* VadeMecum */}
        <fieldset>
          <FieldsetTitle
            title="Funcionalidades do curso"
            icon={BuildOutlined}
            hint="Ative funcionalidades que estarão disponíveis para alunos deste curso"
          />
          <div className="pl-6">
            <Form.Item
              name="has_vade_mecum"
              valuePropName="checked"
              initialValue={false}
              style={{ marginBottom: 0 }}
            >
              <div className="flex items-center justify-between bg-white rounded-lg shadow p-5 border">
                <div>
                  <div className="font-semibold text-base mb-1">
                    Vade Mecum Digital
                  </div>
                  <div className="text-gray-500 text-sm">
                    Biblioteca digital completa pra consulta da constituição,
                    leis e códigos
                  </div>
                </div>
                <Switch
                  onChange={checked =>
                    formProps.form?.setFieldValue('has_vade_mecum', checked)
                  }
                />
              </div>
            </Form.Item>
            {!ssoQcDisabled && (
              <Form.Item
                name={['media', 'sso_qc']}
                valuePropName="checked"
                initialValue={false}
                style={{ marginBottom: 0, marginTop: 16 }}
              >
                <div className="flex items-center justify-between bg-white rounded-lg shadow p-5 border">
                  <div>
                    <div className="font-semibold text-base mb-1">
                      Acesso a questões no Qconcursos
                    </div>
                    <div className="text-gray-500 text-sm">
                      Acesso automático a mesa de estudos e o banco de questões
                      do Qconcursos, sem precisar de novo login (SSO).
                    </div>
                  </div>
                  <Switch
                    onChange={checked =>
                      formProps.form?.setFieldValue(
                        ['media', 'sso_qc'],
                        checked
                      )
                    }
                  />
                </div>
              </Form.Item>
            )}
          </div>
        </fieldset>

        <Divider style={{ margin: '16px 0' }} />

        {/* Personalizacao */}
        <fieldset>
          <FieldsetTitle
            title="Personalização e categorização"
            icon={BgColorsOutlined}
            hint="Adicione um ícone e etiqueta para representar e classificar seu curso. Essas informações ajudam na organização e poderão ser alteradas depois."
          />
          <div className="pl-6">
            <Form.Item
              name={['media', 'tags']}
              label="Etiquetas"
              extra={
                <Typography.Text type="secondary" className="block mt-2">
                  Para editar as etiquetas entre em Loja Online `{'>'}`
                  Categorias
                </Typography.Text>
              }
            >
              <Select
                {...productCategorySelectProps}
                placeholder="Selecione uma ou mais etiquetas..."
                mode="multiple"
              />
            </Form.Item>

            <Form.Item
              name={['media', 'icon']}
              label="Ícone"
              initialValue={icon}
            >
              <Typography.Text
                type="secondary"
                className="block text-secondary-500 mt-0 mb-4"
              >
                Escolha o ícone que irá aparecer no card da tela de cursos do
                aluno
              </Typography.Text>

              <Radio.Group
                rootClassName="course-icons-radio-wrapper"
                onChange={onChangeIcon}
                defaultValue={icon}
                value={icon}
                options={COURSE_ICONS.map((icon, index) => ({
                  value: icon,
                  label: (
                    <Flex vertical gap={4}>
                      <Image
                        preview={false}
                        src={`${COURSE_ICONS_PATH}/${icon}`}
                        alt={icon}
                        width={50}
                        height={50}
                      />
                      {((kind === 'graduate' && index === 2) ||
                        (kind === 'undergraduate' && index === 1)) && (
                        <Typography.Text type="secondary">
                          Padrão
                        </Typography.Text>
                      )}
                    </Flex>
                  )
                }))}
              />
            </Form.Item>
          </div>
        </fieldset>
      </Form>
    </Modal>
  );
};
